import "./VisitsDetails.scss";

import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import React, { useEffect, useMemo, useState } from "react";

import HallAppToast from "../../../commons/HallAppToast";
import ReportAPI from "../ReportAPI";
import ReportHeader from "../../report-full/components/ReportHeader";
import Skeleton from "../../../commons/skeleton/Skeleton";

const reportAPI = new ReportAPI();

// ===== helpers =====
function pad2(n) {
  return String(n).padStart(2, "0");
}

function toISODate(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function parseISODate(iso) {
  const [y, m, day] = iso.split("-").map(Number);
  return new Date(y, m - 1, day);
}

function formatBRDate(iso) {
  const d = parseISODate(iso);
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
}

function weekdayLabelPt(date) {
  return date.toLocaleDateString("pt-BR", { weekday: "long" });
}

function weekdayAbbrPt(date) {
  const map = ["DOM", "SEG", "TER", "QUA", "QUI", "SEX", "SAB"];
  return map[date.getDay()];
}

function buildSeriesFromApi(n, apiRows) {
  // apiRows: [{date:"YYYY-MM-DD", visits:number}, ...]
  const rawByDate = {};
  (apiRows || []).forEach((r) => {
    if (r && r.date) rawByDate[r.date] = { visits: Number(r.visits || 0) };
  });

  // pega o "fim" como a maior data que veio da API (ou hoje)
  let end = new Date();
  end.setHours(0, 0, 0, 0);

  const dates = Object.keys(rawByDate);
  if (dates.length) {
    const maxIso = dates.sort().slice(-1)[0];
    end = parseISODate(maxIso);
    end.setHours(0, 0, 0, 0);
  }

  const days = [];
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(end);
    d.setDate(end.getDate() - i);

    const iso = toISODate(d);
    const v = rawByDate[iso]?.visits ?? 0;

    days.push({
      date: iso,
      visits: v,
      dayNum: d.getDate(),
      dow: weekdayAbbrPt(d),
    });
  }

  return days;
}

function getTop3Days(series) {
  return [...series]
    .sort((a, b) => b.visits - a.visits)
    .slice(0, 3);
}

// ===== custom tick (duas linhas: "10" e "SEG") =====
function CompactDayTick(props) {
  const { x, y, payload } = props;
  const iso = payload && payload.value;
  const d = parseISODate(iso);

  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={12} textAnchor="middle" className="vdTickDay">
        {d.getDate()}
      </text>
      <text x={0} y={0} dy={26} textAnchor="middle" className="vdTickDow">
        {weekdayAbbrPt(d)}
      </text>
    </g>
  );
}

export default function VisitsDetails() {
  const [loading, setLoading] = useState(true);
  const [apiRows, setApiRows] = useState([]);

  useEffect(() => {
    findVisits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function findVisits() {
    setLoading(true);
    try {
      const response = await reportAPI.getReportTotalVisits();
      setApiRows(response?.data || []);
    } catch (_) {
      HallAppToast.genericError();
      setApiRows([]);
    } finally {
      setLoading(false);
    }
  }

  const series15 = useMemo(() => buildSeriesFromApi(15, apiRows), [apiRows]);

  const totals = useMemo(() => {
    const totalVisits = series15.reduce((acc, d) => acc + (Number(d.visits) || 0), 0);
    const avgVisits = totalVisits / (series15.length || 1);

    return {
      totalVisits,
      avgVisits,
      top3: getTop3Days(series15),
    };
  }, [series15]);

  return (
    <div className="vd">
      <ReportHeader name="Clientes Atendidos" />

      <header className="vdHeader">
        <div className="vdTitleWrap">
          <div className="vdTitle">Detalhes de visitas</div>
          <div className="vdSubtitle">Últimos 15 dias</div>
        </div>
      </header>

      {/* KPIs */}
      <section className="vdKPIs">
        <div className="vdKpi">
          <div className="vdKpiLabel">Visitas no período</div>
          <div className="vdKpiValue">
            {loading ? <Skeleton width={90} height={28} /> : Math.round(totals.totalVisits)}
          </div>
        </div>

        <div className="vdKpi">
          <div className="vdKpiLabel">Média diária</div>
          <div className="vdKpiValue">
            {loading ? <Skeleton width={90} height={28} /> : totals.avgVisits.toFixed(1)}
          </div>
        </div>
      </section>

      {/* Chart */}
      <section className="vdPanel">
        <div className="vdPanelHeader">
          <div>
            <div className="vdPanelTitle">Visitas por dia</div>
            <div className="vdPanelSubtitle">Role para ver os 15 dias</div>
          </div>
        </div>

        {loading ? (
          <div className="vdChartSkeleton">
            <Skeleton height={16} width="65%" variant="text" />
            <Skeleton height={220} width="100%" className="vdSkBlock" />
            <Skeleton height={10} width="80%" variant="text" />
          </div>
        ) : (
          <div className="vdChartScroll" aria-label="Gráfico com rolagem horizontal">
            <div className="vdChartInner">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={series15} margin={{ top: 10, right: 12, left: -6, bottom: 24 }}>
                  <defs>
                    <linearGradient id="hallappPurple" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#6D28D9" />
                      <stop offset="100%" stopColor="#A78BFA" />
                    </linearGradient>
                  </defs>

                  <CartesianGrid vertical={false} strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    interval={0}
                    height={38}
                    tick={<CompactDayTick />}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis allowDecimals={false} tickLine={false} axisLine={false} width={28} />
                  <Tooltip
                    cursor={{ fill: "rgba(109, 40, 217, 0.08)" }}
                    contentStyle={{ borderRadius: 12, border: "1px solid #eee" }}
                    labelFormatter={(iso) => {
                      const d = parseISODate(iso);
                      return `${formatBRDate(iso)} • ${weekdayLabelPt(d)}`;
                    }}
                    formatter={(value) => [`${value} visitas`, ""]}
                  />
                  <Bar dataKey="visits" fill="url(#hallappPurple)" radius={[10, 10, 6, 6]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </section>

      {/* Top 3 */}
      <section className="vdPanel">
        <div className="vdPanelHeader">
          <div>
            <div className="vdPanelTitle">Top 3 dias com mais volume</div>
            <div className="vdPanelSubtitle">Os picos do período</div>
          </div>
        </div>

        {loading ? (
          <div className="vdTop3">
            <div className="vdTop3Item">
              <div className="vdRank"><Skeleton width={18} height={18} variant="circle" /></div>
              <div className="vdTop3Body">
                <div className="vdTop3Date"><Skeleton width={180} height={14} /></div>
                <div className="vdTop3Meta"><Skeleton width={90} height={12} /></div>
              </div>
            </div>
            <div className="vdTop3Item">
              <div className="vdRank"><Skeleton width={18} height={18} variant="circle" /></div>
              <div className="vdTop3Body">
                <div className="vdTop3Date"><Skeleton width={180} height={14} /></div>
                <div className="vdTop3Meta"><Skeleton width={90} height={12} /></div>
              </div>
            </div>
            <div className="vdTop3Item">
              <div className="vdRank"><Skeleton width={18} height={18} variant="circle" /></div>
              <div className="vdTop3Body">
                <div className="vdTop3Date"><Skeleton width={180} height={14} /></div>
                <div className="vdTop3Meta"><Skeleton width={90} height={12} /></div>
              </div>
            </div>
          </div>
        ) : (
          <div className="vdTop3">
            {totals.top3.map((d, idx) => {
              const dateObj = parseISODate(d.date);
              return (
                <div className="vdTop3Item" key={d.date}>
                  <div className="vdRank">{idx + 1}</div>
                  <div className="vdTop3Body">
                    <div className="vdTop3Date">
                      {formatBRDate(d.date)}{" "}
                      <span className="vdTop3Dow">• {weekdayLabelPt(dateObj)}</span>
                    </div>
                    <div className="vdTop3Meta">{d.visits} visitas</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
